import { Widget2 } from 'bambooo';
import { BaseMap } from '../Map/BaseMap';
import { AreaOverlayLayer } from '../Map/Layers/AreaOverlayLayer';
import { BathymetryLayer } from '../Map/Layers/BathymetryLayer';
import { IdeeEsLayer } from '../Map/Layers/IdeeEsLayer';
import { MovementTracksLayer } from '../Map/Layers/MovementTracksLayer';
import { OsmBaseLayer } from '../Map/Layers/OsmBaseLayer';
import { SightingHeatmapLayer } from '../Map/Layers/SightingHeatmapLayer';
import { SightingPointsLayer } from '../Map/Layers/SightingPointsLayer';
import { SightingStyles } from '../Map/Styles/SightingStyles';
export { SightingMapObjectType } from '../Map/Styles/SightingStyles';
/**
 * Legacy convenience widget — now a thin compose over {@link BaseMap}
 * and the modular layer classes under `inc/Map/Layers/`. The public
 * surface (load, setView, addSighting, addLineRoute, addRawObject,
 * setMovementTracks, refrech, addAreaByJson, …) is preserved so the
 * Sighting and Tour pages continue to work without changes.
 *
 * New pages should use {@link BaseMap} + the layer modules directly
 * instead of this widget — see the OceanFishingMap page for the
 * preferred pattern.
 */
export class SightingMap extends Widget2 {
    constructor(aelement) {
        /*
         * BaseMap creates its own child div under `aelement`. We hold a
         * reference but never own a separate DOM node ourselves — the
         * `_element` we hand to Widget2 is the same one BaseMap uses.
         */
        const base = new BaseMap(aelement);
        super({ element: base.getElement() });
        /**
         * @protected
         */
        this._loadOptions = {};
        this._base = base;
    }
    load(options) {
        if (options !== undefined) {
            this._loadOptions = options;
        }
        this._base.load({
            initialCenterLonLat: [11.030, 47.739],
            initialZoom: 2.2
        });
        this._osm = new OsmBaseLayer();
        this._base.addLayer(this._osm);
        /*
         * Movements layer is always present (just hidden) so the Sighting
         * page can install a visibility-change listener before any data
         * is loaded.
         */
        this._movements = new MovementTracksLayer();
        this._base.addLayer(this._movements);
        /*
         * Points layer goes in via load() too — the legacy widget didn't
         * require addSighting to come after refrech, so the source must
         * exist immediately.
         */
        this._points = new SightingPointsLayer();
        this._base.addLayer(this._points);
        if (this._loadOptions.useBathymetriemap) {
            this._bathymetry = new BathymetryLayer();
            this._base.addLayer(this._bathymetry);
        }
        /*
         * Spanish relief base-map matches the legacy widget's default
         * (added on every refresh there too) — kept on by default.
         */
        this._ideeEs = new IdeeEsLayer();
        this._base.addLayer(this._ideeEs);
        if (this._loadOptions.useHeatmap) {
            this._heatmap = new SightingHeatmapLayer();
            /*
             * Share the points layer's source so heatmap intensity
             * tracks the rendered sighting set exactly.
             */
            const src = this._points.getSource();
            if (src) {
                this._heatmap.setSource(src);
            }
            this._base.addLayer(this._heatmap);
        }
    }
    unload() {
        this._base.unload();
    }
    disposePopover(andRemove = false) {
        this._base.disposePopover(andRemove);
    }
    setHeight(height) {
        this._base.setHeight(height);
    }
    updateSize() {
        this._base.updateSize();
    }
    setView(viewCenter = null, viewZoom = 12.5) {
        this._base.setView(viewCenter, viewZoom);
    }
    getStyle(name) {
        return SightingStyles.get(name);
    }
    /**
     * @returns the underlying OL map. Escape hatch for code that needs
     * to wire custom interactions or controls.
     */
    getOlMap() {
        return this._base.getOlMap();
    }
    /**
     * @returns the underlying BaseMap. Use this when porting pages to
     * the modular layer API.
     */
    getBaseMap() {
        return this._base;
    }
    addSighting(type, id, content, coordinate) {
        var _a;
        (_a = this._points) === null || _a === void 0 ? void 0 : _a.addSighting(type, id, content, coordinate);
    }
    addLineRoute(coordinates) {
        var _a;
        (_a = this._points) === null || _a === void 0 ? void 0 : _a.addLineRoute(coordinates);
    }
    addRawObject(object) {
        var _a;
        (_a = this._points) === null || _a === void 0 ? void 0 : _a.addRawObject(object);
    }
    clearFeatureList() {
        var _a;
        (_a = this._points) === null || _a === void 0 ? void 0 : _a.clearFeatures();
    }
    async refrech() {
        var _a, _b;
        (_a = this._points) === null || _a === void 0 ? void 0 : _a.refresh();
        /*
         * Heatmap shares the points source but needs an explicit rebuild
         * to re-rasterise its gradient — see SightingHeatmapLayer.refresh.
         */
        (_b = this._heatmap) === null || _b === void 0 ? void 0 : _b.refresh();
    }
    setMovementTracks(movements, append = false) {
        var _a;
        (_a = this._movements) === null || _a === void 0 ? void 0 : _a.setMovements(movements, append);
    }
    clearMovementTracks() {
        var _a;
        (_a = this._movements) === null || _a === void 0 ? void 0 : _a.clear();
    }
    isMovementsLayerVisible() {
        var _a, _b;
        return (_b = (_a = this._movements) === null || _a === void 0 ? void 0 : _a.isVisible()) !== null && _b !== void 0 ? _b : false;
    }
    setOnMovementsVisibilityChange(cb) {
        var _a;
        (_a = this._movements) === null || _a === void 0 ? void 0 : _a.setOnVisibilityChange(cb);
    }
    async addAreaByJson(jsonFileUrl, title, name, visible = false) {
        const layer = new AreaOverlayLayer(name, title, jsonFileUrl, visible);
        this._base.addLayer(layer);
        await layer.loadFromUrl();
    }
}
